import { faHeart, faSmile, faThumbsUp, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { uuid4 } from '@sentry/utils';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { FaHeart, FaSmile, FaThumbsUp } from 'react-icons/fa';
import { useMobileApp } from '../../hooks/useMobileApp';
import { useSelector } from '../../reduxToolkit/typedRedux';

export interface LikeButtonProps {
  onClick?: (icon: IconDefinition) => void;
  ref?: ((instance: LikeButtonHandle | null) => void) | React.MutableRefObject<LikeButtonHandle | null> | null;
  hideMenu?: boolean;
  reactionColor?: string;
  size?: number;
  buttonColor?: string;
}

interface Reaction {
  id: string;
  icon: IconDefinition;
}

interface LikeButtonHandle {
  addEmote: (emote: IconDefinition) => void;
}

const uid = uuid4();

const LikeButton: React.ForwardRefRenderFunction<LikeButtonHandle, LikeButtonProps> = (
  { onClick, hideMenu = true, reactionColor = 'white', size = 25, buttonColor = 'white' },
  ref
) => {
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const { isAppView } = useMobileApp();
  const isMobilePreview = useSelector((state) => state.common.isMobilePreview);

  useImperativeHandle(ref, () => ({
    addEmote(emote: IconDefinition) {
      animateLike(emote);
    },
  }));

  const animateLike = (icon: IconDefinition) => {
    const id = uuid4();

    setReactions([
      {
        id,
        icon,
      },
      ...reactions.filter((_, i) => i < 30),
    ]);
  };

  const onEmoteClick = (icon: IconDefinition) => {
    animateLike(icon);
    onClick?.(icon);
  };

  return (
    <>
      <div id={uid} className="wrapper">
        <div className="animations">
          {reactions.map((reaction) => (
            <div className="animate" key={reaction.id} id={reaction.id}>
              <FontAwesomeIcon color={reactionColor} size="2x" icon={reaction.icon} />
            </div>
          ))}
        </div>
        {hideMenu && <FaThumbsUp color={buttonColor} size={size} />}
        <div className="menu">
          <div className="option">
            <FaHeart size={size} color="#FF2442" onClick={() => onEmoteClick(faHeart)} />
          </div>
          <div className="option">
            <FaSmile size={size} color="#FFB830" onClick={() => onEmoteClick(faSmile)} />
          </div>
          <div className="option">
            <FaThumbsUp size={size} color="#3DB2FF" onClick={() => onEmoteClick(faThumbsUp)} />
          </div>
        </div>
      </div>

      <style jsx>
        {`
          @keyframes float {
            0% {
              opacity: 1;
              top: -15px;
              transform: rotateZ(0deg);
            }
            12% {
              transform: rotateZ(-20deg) scale(0.9);
            }
            24% {
              transform: rotateZ(20deg);
              left: 3px;
              opacity: 0.7;
            }
            40% {
              transform: rotateZ(-11deg);
              left: -3px;
            }
            70% {
              transform: rotateZ(10deg);
              left: 3px;
              opacity: 0.5;
            }
            100% {
              opacity: 0;
              top: -300px;
              transform: rotateZ(-5deg);
            }
          }

          .animate {
            position: absolute;
            animation: float 2s linear forwards;
          }

          .wrapper {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;

            .animations {
              position: absolute;
              top: 0;
              left: 0;
            }

            &:hover .menu {
              display: flex;
            }

            .menu {
              ${hideMenu ? 'display: none;' : 'display:flex;'}
              position: absolute;
              bottom: 0;
              right: ${isAppView() || isMobilePreview || isMobile ? '-100px' : '0'};
              z-index: 1;
              ${hideMenu
                ? `background: #fff;
              border-radius: 100px;`
                : ``}

              @media (max-width: 500px) {
                left: -10px;
                right: auto;
              }

              .option {
                cursor: pointer;
                margin: 3px;
                height: 34px;
                width: 34px;
                display: flex;
                align-items: center;
                justify-content: center;
                transition-duration: 200ms;

                &:hover {
                  background: #ddd;
                  border-radius: 100px;
                }

                &:not(:last-child) {
                  margin-bottom: 3px;
                }
              }
            }
          }
        `}
      </style>
    </>
  );
};

export default forwardRef(LikeButton);
